<script setup lang="ts">
import CitySelector from "@/components/time/CitySelector.vue";
import { useRamadanTime } from "@/composables/useRamadanTime";
import { cities, cityKeys } from "@/data/cities";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

const { cityData, cityKey, setCity } = useRamadanTime();
const { isActive: isEidActive } = useEidWindow();
const route = useRoute();
const router = useRouter();

const availableCities = cityKeys.map((key) => ({
  key,
  name: cities[key].name,
}));

const normalizedRoutePath = computed(
  () => route.path.replace(/\/+$/, "") || "/",
);

const navItems = [
  //{ label: "Eid", to: "/eid/" },
  { label: "Time", to: "/" },
  { label: "Roza Countdown", to: "/roza-countdown/" },
  { label: "Ramadan Kab Shuru Hua", to: "/ramadan-start/" },
  { label: "Calendar", to: "/calendar/" },
  { label: "Shab-e-Qadr", to: "/shab-e-qadr/" },
  { label: "Itikaf", to: "/itikaf/" },
  { label: "Zakat", to: "/zakat/" },
  { label: "Dua", to: "/dua/" },
] as const;

const isActive = (to: string) => {
  const currentPath = normalizedRoutePath.value;

  if (to === "/eid/") {
    return currentPath === "/eid" || (currentPath === "/" && isEidActive.value);
  }

  if (to === "/") {
    if (currentPath === "/" && !isEidActive.value) return true;
    return availableCities.some((c) => currentPath === `/${c.key}`);
  }

  const normalizedTarget = to.replace(/\/+$/, "") || "/";
  return (
    currentPath === normalizedTarget ||
    (normalizedTarget !== "/" && currentPath.startsWith(`${normalizedTarget}/`))
  );
};

const selectCity = (city: { key: string; name: string }) => {
  setCity(city.key as keyof typeof cities);

  if (process.client) {
    localStorage.setItem("ramadan-city-key", city.key);
  }

  const currentPath = normalizedRoutePath.value;
  const isOnRoot = currentPath === "/";
  const isOnCityPath = availableCities.some((c) => currentPath === `/${c.key}`);

  if (isOnRoot || isOnCityPath) {
    router.replace(`/${city.key}/`).catch(() => {});
  }
};
</script>

<template>
  <header
    class="sticky top-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-100 dark:border-gray-800"
  >
    <div class="flex items-center justify-between gap-3 px-4 py-3">
      <!-- Brand / Home -->
      <UButton
        to="/"
        variant="ghost"
        color="neutral"
        size="lg"
        class="px-0 min-h-11 font-semibold text-gray-900 dark:text-white"
      >
        Rozay
      </UButton>

      <!-- Desktop Nav Links (centered) -->
      <nav
        v-cloak
        class="hidden md:flex flex-1 items-center justify-center gap-6 text-sm"
        aria-label="Primary"
      >
        <NuxtLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="px-3 py-1.5 rounded-full transition-colors"
          :class="
            isActive(item.to)
              ? 'bg-emerald-100 text-emerald-800 font-semibold dark:bg-emerald-900/40 dark:text-emerald-200'
              : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
          "
        >
          {{ item.label }}
        </NuxtLink>
      </nav>

      <!-- Right side: context + preference -->
      <div class="flex items-center gap-2">
        <!-- City (contextual, reactive) -->
        <CitySelector
          :cities="availableCities"
          :selectedCityKey="cityKey"
          button-variant="ghost"
          button-color="neutral"
          button-size="xs"
          button-class="min-h-9 px-2.5 text-sm border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/70 hover:bg-gray-50 dark:hover:bg-gray-700"
          icon-class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400"
          chevron-class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400"
          @select="selectCity"
        />

        <!-- Theme switcher (quiet preference) -->
        <div class="opacity-70 hover:opacity-100 transition">
          <ThemSwitcher />
        </div>
      </div>
    </div>
  </header>

  <!-- Mobile Nav Chips -->
  <nav
    v-cloak
    class="flex md:hidden border-b border-gray-100 dark:border-gray-800 px-4 py-2 overflow-x-auto whitespace-nowrap"
    aria-label="Primary"
  >
    <div class="flex items-center gap-2">
      <NuxtLink
        v-for="item in navItems"
        :key="item.to"
        :to="item.to"
        class="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
        :class="
          isActive(item.to)
            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
            : 'bg-gray-100 text-gray-700 hover:text-gray-900 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-gray-100'
        "
      >
        {{ item.label }}
      </NuxtLink>
    </div>
  </nav>
</template>

<style scoped>
[v-cloak] {
  display: none;
}
</style>
